import type { ProductVideoProps } from './ProductVideo';
import type { MouseEvent, PropsWithChildren } from 'react';

import dynamic from 'next/dynamic';
import { useState, useEffect } from 'react';
import { useTheme } from 'styled-components';

import { GALLERY_LAYOUT } from './utils/galleryLayout';
import type { ProductVideo as ProductVideoSchema } from '~/amplienceTypes/schemas/imported/product-video-schema';
import { ProductImage } from '~/components/pdp/productDetail/productImageGallery/ProductImage';
import type { CarouselImageProperty } from '~/components/pdp/productDetail/productImageGallery/ProductImage';
import { Box } from '~/components/shared/core/box/Box';
import { Flex } from '~/components/shared/core/flex/Flex';
import { useNavActionContext } from '~/utilities/context/dynamic/NavActionContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import type { Maybe, Video } from '~/utilities/graphql/codegen';

const ProductVideo = dynamic<ProductVideoProps>(
  () =>
    import('../../../pdp/productDetail/productImageGallery/ProductVideo').then(
      mod => mod.ProductVideo
    ),
  { ssr: false }
);

interface LargeDesktopLayoutProps extends PropsWithChildren {
  unpackedGallery: boolean;
  productImageList: CarouselImageProperty[];
  showAllImages?: boolean;
  video?: Maybe<Video>;
  productVideoContent: ProductVideoSchema;
  videoIndex: number;
  isImagePair: boolean;
  setCurrentImageIndex: (idx: number) => void;
  setZoomPanelScrollXCenter: (e: MouseEvent) => void;
  gap: string;
  largeDesktopBreakpoint: number;
}

export const LargeDesktopLayout = ({
  unpackedGallery,
  productImageList,
  showAllImages,
  video,
  productVideoContent,
  videoIndex,
  isImagePair,
  setCurrentImageIndex,
  setZoomPanelScrollXCenter,
  gap,
  largeDesktopBreakpoint,
  children,
}: LargeDesktopLayoutProps) => {
  const theme = useTheme();
  const { sideNavOpened } = useNavActionContext();
  const { siteType } = useAppContext();
  const [imageBoxHeight, setImageBoxHeight] = useState<string>('auto');
  const { padding, columnWidth } = GALLERY_LAYOUT[siteType];

  useEffect(() => {
    if (unpackedGallery || window.innerWidth < Number.parseInt(theme.breakpoints.xxxl, 10)) {
      return undefined;
    }

    const handleGalleryWidthChange = () => {
      const sideNavWidth = document.getElementById('sideNav')?.getBoundingClientRect().width || 0;
      const detailsWidth =
        document.querySelector('.product-detail-info-wrapper')?.getBoundingClientRect().width || 0;
      const galleryWidth = window.innerWidth - sideNavWidth - detailsWidth;
      const maxImageBoxWidth = (950 * 4) / 5;
      const imageBoxHeight = galleryWidth > maxImageBoxWidth * 2 ? 'auto' : '950px';

      setImageBoxHeight(imageBoxHeight);
    };

    window.addEventListener('resize', handleGalleryWidthChange);

    handleGalleryWidthChange();

    return () => {
      window.removeEventListener('resize', handleGalleryWidthChange);
    };
  }, [sideNavOpened, unpackedGallery, theme]);

  const totalGalleryItems = productImageList.length + (video ? 1 : 0);
  const lastImageIdx = productImageList.length - 1;
  const lastImageSpansFullRow = totalGalleryItems % 2 !== 0;

  return (
    <Flex padding={padding} $flexDirection="row" $flexWrap="wrap" $gap={gap}>
      {unpackedGallery && video && (
        <Box
          $w={columnWidth}
          $h="auto"
          style={{
            overflow: 'hidden',
            position: 'relative',
            order: videoIndex,
          }}
        >
          {children}

          <ProductVideo
            {...productVideoContent}
            active
            video={video}
            videoIndex={videoIndex}
            showAllImages={showAllImages}
          />
        </Box>
      )}

      {unpackedGallery
        ? productImageList.map(({ original }, idx) => {
            const isLastElement = lastImageSpansFullRow && idx === lastImageIdx;
            const isHidden = !showAllImages && (video && idx >= videoIndex ? idx + 1 : idx) > 3;

            return (
              <Box
                key={original.url}
                $w={isLastElement ? '100%' : columnWidth}
                $h="100%"
                aria-hidden={isHidden}
                style={{
                  backgroundColor: 'var(--image-background-placeholder)',
                  opacity: isHidden ? '0' : '1',
                  transition: 'opacity 0.3s ease-in-out',
                  transitionDelay: '0.3s',
                  order: video && idx > videoIndex ? idx + 1 : idx,
                }}
              >
                <Box
                  $w={isLastElement && productImageList.length > 1 ? columnWidth : '100%'}
                  $h="100%"
                  style={{
                    overflow: 'hidden',
                    position: 'relative',
                    margin: isLastElement ? '0 auto' : 'none',
                  }}
                  onClick={(e: MouseEvent) => {
                    setCurrentImageIndex(idx);
                    setZoomPanelScrollXCenter(e);
                  }}
                >
                  {idx === 0 && !video ? children : null}

                  <ProductImage
                    original={{ ...original, tabIndex: isHidden ? -1 : 0, dataImageIndex: idx }}
                    largeDesktopBreakpoint={largeDesktopBreakpoint}
                  />
                </Box>
              </Box>
            );
          })
        : productImageList.map(({ original, lazyLoad }, idx) => (
            <Box
              key={original.url}
              $w={isImagePair ? columnWidth : '100%'}
              $h={imageBoxHeight}
              style={{
                overflow: 'hidden',
              }}
              onClick={(e: MouseEvent) => {
                setCurrentImageIndex(idx);
                setZoomPanelScrollXCenter(e);
              }}
            >
              <ProductImage
                original={original}
                lazyLoad={lazyLoad}
                largeDesktopBreakpoint={largeDesktopBreakpoint}
                isFullWidthImage={!isImagePair}
              />
            </Box>
          ))}
    </Flex>
  );
};
